"use client";

import { trackReportInputCollectionStarted } from "@/lib/analytics-agent-offers";
import { offerPresentationId } from "@/lib/analytics-offer-milestones";
import { safeSessionSet } from "@/lib/browser-storage";
import { journeyActionEventProperties } from "@/lib/governed-action";
import {
  ISHVARAM_COMMERCE_SYSTEM,
  ISHVARAM_REPORT_CHECKOUT_CHANNEL,
} from "@/lib/commerce-attribution";
import type { PaidReportCTAProps } from "./PaidReportCTA.types";

type CollectInputsArgs = Pick<PaidReportCTAProps, "analyticsContext" | "planId"> & {
  href: string;
  presentationId?: string;
};

export function startReportInputCollection({
  analyticsContext,
  href,
  planId,
  presentationId,
}: CollectInputsArgs) {
  const sourcePath = inputCollectionSourcePath(analyticsContext);
  persistReportInputIntent({ analyticsContext, planId, sourcePath });
  trackReportInputCollection({ analyticsContext, planId, presentationId, sourcePath });
  window.location.href = href;
}

function inputCollectionSourcePath(
  analyticsContext: PaidReportCTAProps["analyticsContext"]
): string | undefined {
  if (analyticsContext?.sourcePath) return analyticsContext.sourcePath;
  return typeof window !== "undefined" ? window.location.pathname || undefined : undefined;
}

function persistReportInputIntent({
  analyticsContext,
  planId,
  sourcePath,
}: Pick<CollectInputsArgs, "analyticsContext" | "planId"> & { sourcePath?: string }) {
  try {
    if (analyticsContext?.intent) safeSessionSet("seo_entry_intent", analyticsContext.intent);
    if (sourcePath) safeSessionSet("seo_entry_path", sourcePath);
    safeSessionSet("seo_entry_primary_offer", "report");
    safeSessionSet("seo_entry_report_plan_id", planId);
  } catch {
    // Navigation still works when session storage is unavailable.
  }
}

function trackReportInputCollection({
  analyticsContext,
  planId,
  presentationId,
  sourcePath,
}: Pick<CollectInputsArgs, "analyticsContext" | "planId" | "presentationId"> & {
  sourcePath?: string;
}) {
  const {
    agentTraceId,
    checkoutChannel = ISHVARAM_REPORT_CHECKOUT_CHANNEL,
    commerceSystem = ISHVARAM_COMMERCE_SYSTEM,
    customerProblem,
    intent,
    placement = "report_landing_primary_offer",
    rankedOfferOrder,
    routeRankReason,
    routeRankScore,
  } = analyticsContext ?? {};
  const resolvedSourcePath = sourcePath ?? "/";
  const resolvedPresentationId =
    presentationId ??
    analyticsContext?.presentationId ??
    offerPresentationId({
      actionMode: "input_collection",
      planId,
      placement,
      sourcePath: resolvedSourcePath,
    });
  // NAVIGATION, not a buying tap — this routes to the birth-details form, so it
  // must never enter offer_cta_tapped / CTR (see the tracker's docstring and
  // data/contracts/offer_milestone_events.yaml).
  trackReportInputCollectionStarted({
    source_path: sourcePath,
    intent,
    plan_id: planId,
    placement,
    checkout_channel: checkoutChannel,
    commerce_system: commerceSystem,
    customer_problem: customerProblem,
    route_rank_score: routeRankScore,
    route_rank_reason: routeRankReason,
    ranked_offer_order: rankedOfferOrder,
    agent_trace_id: agentTraceId,
    presentation_id: resolvedPresentationId,
    ...journeyActionEventProperties(analyticsContext),
  });
}
